import React, { useState, useEffect } from 'react'
import { Table, Segmented, Input, DatePicker, GetProps } from 'antd'
import useStationStore from '@/stores/oneStation'
import useGlobalConfigStore from '@/stores/globalConfig'
import getDeviceEvents from '@/utils/getDeviceEvents'
import dayjs, { Dayjs } from 'dayjs'
import useMessage from '@/hooks/useMessage'
import formatDate from '@/utils/formatDate'
import { useTranslation } from 'react-i18next'

const { RangePicker } = DatePicker
const { Search } = Input
type RangePickerProps = GetProps<typeof DatePicker.RangePicker>

const Message: React.FC = () => {
  const { t } = useTranslation()
  interface DataType {
    key: string
    message: string
    type: string
    level: number
    deviceName: string
    occurrenceTime: string
    recoveryTime: string
  }
  const dateFormat = 'DD/MM/YYYY'

  const device = useStationStore(state => state.device)
  const initFinished = useGlobalConfigStore(state => state.initFinished)
  const setUpdateTime = useGlobalConfigStore(state => state.setUpdateTime)
  const { message } = useMessage()
  const [eventStatus, setEventStatus] = useState(0)

  const currentTime = new Date(),
    yesterdayTime = new Date()
  yesterdayTime.setDate(currentTime.getDate() - 1)
  const [timeRanger, setTimeRanger] = useState<[Dayjs, Dayjs]>([dayjs(yesterdayTime), dayjs(currentTime)])

  const [searchInput, setSearchInput] = useState('')
  const [columns, setColumns] = useState<any[]>([
    {
      title: t('message.message'),
      dataIndex: 'message',
    },
    {
      title: t('message.type'),
      dataIndex: 'type',
    },
    {
      title: t('message.level'),
      dataIndex: 'level',
    },
    {
      title: t('device.deviceName'),
      dataIndex: 'deviceName',
    },
    {
      title: t('message.occurrenceTime'),
      dataIndex: 'occurrenceTime',
    },
    {
      title: t('message.recoveryTime'),
      dataIndex: 'recoveryTime',
      hidden: true,
    },
  ])
  const [tableData, setTableData] = useState<DataType[]>([])
  const [showTableData, setShowTableData] = useState<DataType[]>([])

  const getTableData = async () => {
    if (initFinished && device.length > 0 && timeRanger.length > 0) {
      const start = timeRanger[0].valueOf()
      const end = timeRanger[1].hour(23).minute(59).second(59).millisecond(999).valueOf()
      const deviceId = device.map(item => item.device_id)
      const data: any = { type: ['alarm'], deviceId, start, end }
      if (eventStatus !== 2) {
        data.state = eventStatus
      }
      try {
        const res: any = await getDeviceEvents(data)
        // console.log(res)
        const tableDataTemp: DataType[] = []
        res.data.forEach((item: any) => {
          tableDataTemp.push({
            key: item.rowKey,
            message: item.detail.msg,
            type: item.type,
            level: item.level,
            deviceName: item.detail?.deviceName,
            occurrenceTime: formatDate(new Date(item.timestamp)),
            recoveryTime: item.state ? formatDate(new Date(item.recovery)) : '/',
          })
        })
        setTableData(tableDataTemp)
        setShowTableData(tableDataTemp)
        setColumns(prev => {
          const newColumns = [...prev]
          const target = newColumns.find(item => item.dataIndex === 'recoveryTime')
          if (target) {
            if (eventStatus === 0) {
              target.hidden = true
            } else {
              target.hidden = false
            }
          }
          return newColumns
        })
        setUpdateTime()
        setSearchInput('')
      } catch (e) {
        console.log(e)
        message.error('Get Device Events Error')
      }
    }
  }

  useEffect(() => {
    getTableData()
  }, [device, eventStatus, timeRanger, t])

  useEffect(() => {
    const tableDataTemp = tableData.filter(item => item.message.includes(searchInput))
    setShowTableData(tableDataTemp)
  }, [searchInput])

  const timeChange = (chosenTime: any) => {
    setTimeRanger(chosenTime)
  }

  const disabledDate: RangePickerProps['disabledDate'] = current => {
    return current > dayjs().endOf('day')
  }

  return (
    <div id='message'>
      <div className='message-header'>
        <Segmented
          onChange={v => setEventStatus(v)}
          value={eventStatus}
          style={{ marginBottom: 8 }}
          options={[
            {
              label: (
                <div className='Message-Segmented-status'>
                  <div className='yuan error'></div>
                  <div>{t('message.pending')}</div>
                </div>
              ),
              value: 0,
            },
            {
              label: (
                <div className='Message-Segmented-status'>
                  <div className='yuan success'></div>
                  <div>{t('message.resolved')}</div>
                </div>
              ),
              value: 1,
            },
            {
              label: (
                <div className='Message-Segmented-status'>
                  <div>{t('common.all')}</div>
                </div>
              ),
              value: 2,
            },
          ]}
        />
        <div className='messageFilter'>
          <RangePicker
            style={{ width: '300px' }}
            suffixIcon={<img src='/assets/image/date.svg' />}
            size='small'
            onChange={timeChange}
            format={dateFormat}
            value={timeRanger}
            disabledDate={disabledDate}
          />
          <Search
            style={{ width: '300px' }}
            placeholder={t('message.warningFaultName')}
            enterButton
            onSearch={setSearchInput}
          />
        </div>
      </div>
      <div className='message-table-container'>
        <Table
          style={{ height: '100%' }}
          scroll={{
            y: '100%',
          }}
          dataSource={showTableData}
          columns={columns}
          pagination={false}
          tableLayout='auto'
        />
      </div>
    </div>
  )
}

export default Message
